import { useState } from 'react';
import { AlertCircle, CheckCircle, Clock, Edit, Globe, Trash2, TrendingUp, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import type { MonitoredSite } from '../../types';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface SiteCardProps {
  site: MonitoredSite;
  isChecking: boolean;
  onEdit: (site: MonitoredSite) => void;
  onDelete: (site: MonitoredSite) => void;
  onCheckNow: (id: string) => void;
  onViewHistory: (site: MonitoredSite) => void;
}

export function SiteCard({ site, isChecking, onEdit, onDelete, onCheckNow, onViewHistory }: SiteCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const status = site.latestCheck?.status || 'CRITICAL';
  const hasIssues = site.latestCheck?.issues && site.latestCheck.issues.length > 0;
  const hasDnsInfo = site.latestCheck?.dnsIps && site.latestCheck.dnsIps.length > 0;

  const statusColors = {
    HEALTHY: 'bg-green-500 dark:bg-green-600',
    WARNING: 'bg-yellow-500 dark:bg-yellow-600',
    CRITICAL: 'bg-red-500 dark:bg-red-600'
  };

  const statusIcons = {
    HEALTHY: <CheckCircle className="h-4 w-4" />,
    WARNING: <AlertCircle className="h-4 w-4" />,
    CRITICAL: <XCircle className="h-4 w-4" />
  };

  const statusLabels = {
    HEALTHY: 'Operacional',
    WARNING: 'Aviso',
    CRITICAL: 'Fora do ar'
  };

  const truncateUrl = (url: string, maxLength = 40) => {
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true, locale: ptBR });
    } catch {
      return 'Data inválida';
    }
  };

  return (
    <Card className="relative overflow-hidden">
      {/* Status indicator bar */}
      <div className={`absolute top-0 left-0 right-0 h-1 ${statusColors[status]}`} />

      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{site.name}</span>
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1" title={site.url}>
              {truncateUrl(site.url)}
            </p>
          </div>
          <Badge
            variant="secondary"
            className={`${statusColors[status]} text-white border-none flex items-center gap-1 flex-shrink-0`}
          >
            {statusIcons[status]}
            {statusLabels[status]}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Metrics */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Resposta:</span>
            <span className="font-medium">
              {site.latestCheck?.responseTimeMs ? `${site.latestCheck.responseTimeMs}ms` : 'N/A'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {site.latestCheck?.sslValid === true && (
              <>
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-muted-foreground">SSL válido</span>
              </>
            )}
            {site.latestCheck?.sslValid === false && (
              <>
                <XCircle className="h-4 w-4 text-red-500" />
                <span className="text-muted-foreground">SSL inválido</span>
              </>
            )}
            {site.latestCheck?.sslValid === undefined && (
              <span className="text-muted-foreground">SSL: N/A</span>
            )}
          </div>
        </div>

        {/* Last check time */}
        {site.latestCheck?.checkedAt && (
          <p className="text-xs text-muted-foreground">
            Última verificação: {formatTimestamp(site.latestCheck.checkedAt)}
          </p>
        )}

        {/* Expandable section */}
        {(hasIssues || hasDnsInfo) && (
          <div className="border-t pt-3">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full"
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              <span>Detalhes</span>
            </button>

            {isExpanded && (
              <div className="mt-3 space-y-2">
                {hasIssues && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Problemas:</p>
                    <ul className="text-xs space-y-1">
                      {site.latestCheck?.issues?.map((issue, idx) => (
                        <li key={idx} className="text-red-600 dark:text-red-400">
                          • {issue}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {hasDnsInfo && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">IPs DNS:</p>
                    <ul className="text-xs space-y-1">
                      {site.latestCheck?.dnsIps?.map((ip, idx) => (
                        <li key={idx} className="text-muted-foreground">
                          • {ip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onCheckNow(site.id)}
            disabled={isChecking || !site.enabled}
          >
            <TrendingUp className="h-3 w-3 mr-1" />
            {isChecking ? 'Verificando...' : 'Verificar Agora'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onViewHistory(site)}
          >
            <Clock className="h-3 w-3 mr-1" />
            Histórico
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onEdit(site)}
          >
            <Edit className="h-3 w-3 mr-1" />
            Editar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(site)}
            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          >
            <Trash2 className="h-3 w-3 mr-1" />
            Excluir
          </Button>
        </div>

        {!site.enabled && (
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded p-2 text-xs text-yellow-700 dark:text-yellow-400">
            Este site está desabilitado e não será monitorado.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
